import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ApiResponse,
  GetConfirmRegistrationInfoResponse,
} from '@models/ApiResponse';
import { AppError } from '@models/appError';
import { LmsApi } from '@services/_api/lms-api';
import {
  ApiEndpointsService,
  ApiHttpErrorService,
  ApiHttpService,
  AuthenticationService,
  HttpSpinnerService,
  RouteNavigationService,
  ToastService,
} from '@services/index';
import { WebcamImage } from 'ngx-webcam';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

interface CreateProfileImageRequest {
  contentType: string;
  fileName: string;
  base64File: string;
}

@Component({
  selector: 'app-onboarding, dialog-elements-example',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.css'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
})
export class OnboardingComponent implements OnInit, AfterViewInit {
  @ViewChild('signaturePad') signaturePad: ElementRef;
  @ViewChild('initialsPad') initialsPad: ElementRef;

  updatedProfilePicture: string | null = null;

  public uploading = false;
  public filesForm: FormGroup;
  public documents: any[];
  public currentUser: LmsApi.AuthenticateResponse;
  public pictureTaken = new EventEmitter<WebcamImage>();

  signatureSvg: SVGElement;
  signatureSvgContainer: HTMLElement;
  signaturePath: HTMLElement;
  signaturePadHeight = '250';
  signaturePadWidth = '300';
  title = 'angular-image-uploader';

  initialsSvg: SVGElement;
  initialsSvgContainer: HTMLElement;
  initialsPath: HTMLElement;
  initialsPadHeight = '150';
  initialsPadWidth = '250';

  submitted = false;
  confirmRegistrationInfo: GetConfirmRegistrationInfoResponse;
  token: string;
  accountRegistrationCompleted = false;
  showPassword = false;
  showConfirmPassword = false;

  activeDocs = true;
  showWebcam: any;

  public usersProfileImage: string | null = null;

  userSelectedImageEvent: any;

  passwordFormGroup = this._formBuilder.group({
    password: [null, Validators.required],
    confirmPassword: [null, Validators.required],
    acceptTerms: [null, Validators.required],
  });

  dialog: any;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private httpService: ApiHttpService,
    private endpointsService: ApiEndpointsService,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private httpSpinnerService: HttpSpinnerService,
    private routeNavService: RouteNavigationService,
    private httpErrorService: ApiHttpErrorService,
    private _formBuilder: FormBuilder
  ) {}

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return this.accountRegistrationCompleted;
  }

  ngAfterViewInit() {
    this.initInitialsPad();
    this.initSignaturePad();
  }

  ngOnInit() {
    this.initPasswordForm();
    this.token = this.route.snapshot.queryParams['token'];
    this.httpSpinnerService.show();
    this.httpService
      .post<ApiResponse<GetConfirmRegistrationInfoResponse>>(
        this.endpointsService.getConfirmRegInfo(),
        {
          token: this.token,
        }
      )
      .pipe(
        take(1),
        finalize(() => {
          this.httpSpinnerService.hide();
        })
      )
      .subscribe(
        (res) => {
          this.confirmRegistrationInfo = res.data;
        },
        (error: AppError) => {
          this.httpErrorService.handle(error);
          this.accountRegistrationCompleted = true;
          this.routeNavService.goToHome();
        }
      );
  }

  public tryCompleteRegistration() {
    let errors = false;
    this.submitted = true;
    if (!this.acceptTerms) {
      this.toastService.showError(
        'Terms and conditions have not been accepted'
      );
      errors = true;
    }

    if (this.passwordFormGroup.invalid) {
      errors = true;
      this.passwordFormGroup.markAllAsTouched();
      this.toastService.missingRequiredFields();
    }

    if (!this.passwordsMatch) {
      errors = true;
      this.passwordFormGroup.markAllAsTouched();
      this.toastService.showError('Passwords do not match');
    }

    if (this.signatureSvg.innerHTML === '') {
      this.toastService.showError('Your signature is required');
      errors = true;
    }
    if (this.initialsSvg.innerHTML === '') {
      this.toastService.showError('Your initials are required');
      errors = true;
    }
    if (errors) {
      return;
    }

    this.completeRegistration();
  }

  public resetSignature() {
    this.signatureSvg.innerHTML = '';
  }

  public resetInitials() {
    this.initialsSvg.innerHTML = '';
  }

  // each time the user selects a picture in the app image picker, it ends up reaching here
  public pictureChosen(profilePicture: string | null) {
    this.usersProfileImage = profilePicture;
  }

  get password() {
    return this.passwordFormGroup?.get('password');
  }

  get confirmPassword() {
    return this.passwordFormGroup?.get('confirmPassword');
  }

  get acceptTerms() {
    return this.passwordFormGroup?.get('acceptTerms');
  }

  get passwordsMatch() {
    return this.password.value === this.confirmPassword.value;
  }

  private initInitialsPad() {
    const initialsPad: HTMLElement = this.initialsPad.nativeElement;
    this.initialsSvgContainer = initialsPad.querySelector(
      '#initialsSvgContainer'
    );
    this.initialsSvg = this.initialsSvgContainer.querySelector('svg');

    this.renderer.setAttribute(
      this.initialsSvg,
      'height',
      this.initialsPadHeight
    );
    this.renderer.setAttribute(
      this.initialsSvg,
      'width',
      this.initialsPadWidth
    );
    this.renderer.setAttribute(
      this.initialsSvg,
      'viewBox',
      `0 0 ${this.initialsPadWidth} ${this.initialsPadHeight}`
    );

    this.renderer.listen(this.initialsSvg, 'mousedown', (event) => {
      this.initialsPath = this.createSvgElement('polyline');
      this.renderer.setAttribute(this.initialsPath, 'fill', 'none');
      this.renderer.setAttribute(
        this.initialsPath,
        'shape-rendering',
        'geometricPrecision'
      );
      this.renderer.setAttribute(this.initialsPath, 'stroke', '#0b0a0a');
      this.renderer.setAttribute(this.initialsPath, 'stroke-width', '4');
      this.renderer.setAttribute(this.initialsPath, 'stroke-linecap', 'round');
      this.renderer.setAttribute(
        this.initialsPath,
        'points',
        event.offsetX + ',' + event.offsetY
      );
      this.renderer.appendChild(this.initialsSvg, this.initialsPath);
    });

    this.renderer.listen(this.initialsSvg, 'mousemove', (event) => {
      if (this.initialsPath) {
        let pathData = this.initialsPath.getAttribute('points');
        pathData = pathData + ' ' + event.offsetX + ',' + event.offsetY;
        this.renderer.setAttribute(this.initialsPath, 'points', pathData);
      }
    });

    this.renderer.listen(this.initialsSvg, 'mouseup', (event) => {
      if (this.initialsPath) {
        let pathData = this.initialsPath.getAttribute('points');
        pathData = pathData + ' ' + event.offsetX + ',' + event.offsetY;
        this.renderer.setAttribute(this.initialsPath, 'points', pathData);
        this.initialsPath = null;
      }
    });

    this.renderer.listen(this.initialsSvg, 'mouseleave', (event) => {
      if (this.initialsPath) {
        let pathData = this.initialsPath.getAttribute('points');
        pathData = pathData + ' ' + event.offsetX + ',' + event.offsetY;
        this.renderer.setAttribute(this.initialsPath, 'points', pathData);
        this.initialsPath = null;
      }
    });
  }

  private initSignaturePad() {
    const signaturePad: HTMLElement = this.signaturePad.nativeElement;
    this.signatureSvgContainer = signaturePad.querySelector('#sigSvgContainer');
    this.signatureSvg = this.signatureSvgContainer.querySelector('svg');

    this.renderer.setAttribute(
      this.signatureSvg,
      'height',
      this.signaturePadHeight
    );
    this.renderer.setAttribute(
      this.signatureSvg,
      'width',
      this.signaturePadWidth
    );
    this.renderer.setAttribute(
      this.signatureSvg,
      'viewBox',
      `0 0 ${this.signaturePadWidth} ${this.signaturePadHeight}`
    );

    this.renderer.listen(this.signatureSvg, 'mousedown', (event) => {
      this.signaturePath = this.createSvgElement('polyline');
      this.renderer.setAttribute(this.signaturePath, 'fill', 'none');
      this.renderer.setAttribute(
        this.signaturePath,
        'shape-rendering',
        'geometricPrecision'
      );
      this.renderer.setAttribute(this.signaturePath, 'stroke', '#0b0a0a');
      this.renderer.setAttribute(this.signaturePath, 'stroke-width', '4');
      this.renderer.setAttribute(this.signaturePath, 'stroke-linecap', 'round');
      this.renderer.setAttribute(
        this.signaturePath,
        'points',
        event.offsetX + ',' + event.offsetY
      );
      this.renderer.appendChild(this.signatureSvg, this.signaturePath);
    });

    this.renderer.listen(this.signatureSvg, 'mousemove', (event) => {
      if (this.signaturePath) {
        let pathData = this.signaturePath.getAttribute('points');
        pathData = pathData + ' ' + event.offsetX + ',' + event.offsetY;
        this.renderer.setAttribute(this.signaturePath, 'points', pathData);
      }
    });

    this.renderer.listen(this.signatureSvg, 'mouseup', (event) => {
      if (this.signaturePath) {
        let pathData = this.signaturePath.getAttribute('points');
        pathData = pathData + ' ' + event.offsetX + ',' + event.offsetY;
        this.renderer.setAttribute(this.signaturePath, 'points', pathData);
        this.signaturePath = null;
      }
    });

    this.renderer.listen(this.signatureSvg, 'mouseleave', (event) => {
      if (this.signaturePath) {
        let pathData = this.signaturePath.getAttribute('points');
        pathData = pathData + ' ' + event.offsetX + ',' + event.offsetY;
        this.renderer.setAttribute(this.signaturePath, 'points', pathData);
        this.signaturePath = null;
      }
    });
  }

  private async completeRegistration() {
    const completeRegistrationRequest = {
      signatureSvg: this.signatureSvgContainer.innerHTML.replace(
        /#0b0a0a/gi,
        '{SVG_FILL_COLOUR}'
      ),
      initialsSvg: this.initialsSvgContainer.innerHTML.replace(
        /#0b0a0a/gi,
        '{SVG_FILL_COLOUR}'
      ),
      password: this.password.value,
      confirmPassword: this.confirmPassword.value,
      acceptTerms: this.acceptTerms.value,
      accountId: this.confirmRegistrationInfo.accountId,
      tenantId: this.confirmRegistrationInfo.tenantId,
      token: this.token,
      profileImage: this.usersProfileImage,
    };

    this.httpService
      .post<ApiResponse<LmsApi.AuthenticateResponse>>(
        this.endpointsService.confirmRegistration(),
        completeRegistrationRequest
      )
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (res.succeeded && res.data.jwtToken) {
            this.accountRegistrationCompleted = true;
            this.toastService.showSuccess('Account registration finalized!');
            this.authService.updateUserSubject(res.data);
            sessionStorage.setItem(
              'currentTenant',
              JSON.stringify(res.data.tenant)
            );
            this.authService.updateTenantSubject(res.data.tenant);
            this.authService.startRefreshTokenTimer(
              res.data.jwtToken,
              res.data.id
            );
          }
          this.routeNavService.login();
        },
        (error: AppError) => {
          this.httpErrorService.handle(error);
        }
      );
  }

  private createSvgElement(tagName: string) {
    return this.renderer.createElement(tagName, 'http://www.w3.org/2000/svg');
  }

  private initPasswordForm() {
    this.passwordFormGroup = this.fb.group({
      password: [
        '',
        [
          Validators.required,
          Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$'),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$'),
        ],
      ],
      acceptTerms: [null, Validators.required],
    });
  }
}
